import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "../admin/Admin.module.css";
import type { ProviderAllocationOverview } from "../../api/providerAllocation";
import type { AccessGroupCatalog } from "./useAccessGroupCatalog";
import {
  emptyEntry,
  formatNumber,
  formatUsd,
  isAmountAcceptable,
  isGroupSelectionMissing,
  isValidAmount,
  minimumAmount,
  type AllocationFigures,
  type Draft,
  type DraftEntry,
} from "./allocationDraft";

/**
 * The shared body of the provider allocation form: the four figures and one row per provider, in two columns.
 * It is controlled; the parent owns the draft and supplies the actions (save, request, cancel).
 *
 * A provider that is on and metered gets an editable limit. A flat-fee provider that is on shows its fixed
 * fee. A provider that is off shows, read-only, what it has already cost this month (zero if nothing); the
 * amount saved for it is still zero.
 *
 * A provider granted per access group that offers exactly one group shows no group choice: switching it on grants that group,
 * and a draft that holds anything else for it (nothing, or a group since renamed) is corrected to it.
 */
export function ProviderAllocationEditor({
  overview,
  draft,
  figures,
  idPrefix,
  accessGroupCatalog,
  onChange,
}: {
  overview: ProviderAllocationOverview;
  draft: Draft;
  figures: AllocationFigures;
  idPrefix: string;
  /** The groups each provider offers; a provider granted per group shows one checkbox per group once it is switched on. */
  accessGroupCatalog?: AccessGroupCatalog;
  onChange: (vendorId: string, change: Partial<DraftEntry>) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const overCommitted = figures.remaining < 0;

  useEffect(() => {
    for (const provider of overview.providers) {
      const offered = accessGroupCatalog?.[provider.vendorId] ?? [];
      const entry = draft[provider.vendorId];
      if (
        provider.supportsAccessGroups === true &&
        offered.length === 1 &&
        entry?.enabled &&
        (entry.accessGroups.length !== 1 || entry.accessGroups[0] !== offered[0].id)
      ) {
        onChange(provider.vendorId, { accessGroups: [offered[0].id] });
      }
    }
  }, [overview, draft, accessGroupCatalog, onChange]);

  return (
    <>
      <dl className={styles.allocationFigures}>
        <div className={styles.allocationFigure}>
          <dt>{t("admin.entitlement.person.allocation.total")}</dt>
          <dd>{formatUsd(overview.totalUsd)}</dd>
        </div>
        <div className={styles.allocationFigure}>
          <dt>{t("admin.entitlement.person.allocation.committed")}</dt>
          <dd>{formatUsd(figures.committed)}</dd>
        </div>
        <div className={styles.allocationFigure}>
          <dt>{t("admin.entitlement.person.allocation.remaining")}</dt>
          <dd>{formatUsd(figures.remaining)}</dd>
        </div>
      </dl>

      <div className={styles.allocationGrid}>
        {overview.providers.map((provider) => {
          const entry = draft[provider.vendorId] ?? emptyEntry;
          const label = provider.displayName;
          const amountId = `${idPrefix}-${provider.vendorId}`;
          const errorId = `${amountId}-error`;
          const editable = entry.enabled && provider.supportsLimit;
          const minimum = minimumAmount(provider);
          const invalid =
            editable && !isAmountAcceptable(provider, entry.amount);
          const showGroups =
            provider.supportsAccessGroups === true &&
            entry.enabled &&
            (accessGroupCatalog?.[provider.vendorId] ?? []).length !== 1;
          const groupsMissing = isGroupSelectionMissing(provider, entry);
          const groupsErrorId = `${amountId}-groups-error`;
          const readOnlyValue = entry.enabled
            ? provider.seatFeeUsd
            : (provider.incurredCostUsd ?? 0);
          const readOnlyLabel = entry.enabled
            ? t(
                "admin.entitlement.person.allocation.fixedCost",
                "Fixed cost for {{provider}}",
                { provider: label },
              )
            : t(
                "admin.entitlement.person.allocation.incurredCost",
                "Cost this month for {{provider}}",
                { provider: label },
              );
          return (
            <div key={provider.vendorId} className={styles.allocationRow}>
              <div className={styles.allocationRowName}>
                <span>{label}</span>
                {provider.seatFeeUsd > 0 && (
                  <span className={styles.note}>
                    {t(
                      provider.supportsLimit
                        ? "admin.entitlement.person.allocation.seatFee"
                        : "admin.entitlement.person.allocation.flatFee",
                      { amount: formatUsd(provider.seatFeeUsd) },
                    )}
                  </span>
                )}
              </div>
              <input
                type="checkbox"
                className={styles.allocationToggle}
                aria-label={t("admin.entitlement.person.allocation.enable", {
                  provider: label,
                })}
                checked={entry.enabled}
                onChange={(event) =>
                  onChange(provider.vendorId, {
                    enabled: event.target.checked,
                    ...(event.target.checked && provider.supportsLimit
                      ? {
                          amount: String(
                            Math.max(
                              minimum,
                              isValidAmount(entry.amount)
                                ? Number.parseInt(entry.amount, 10)
                                : 0,
                            ),
                          ),
                        }
                      : {}),
                  })
                }
              />
              <div className={styles.allocationAmount}>
                <span
                  className={styles.allocationAmountPrefix}
                  aria-hidden="true"
                >
                  $
                </span>
                {editable ? (
                  <input
                    id={amountId}
                    type="number"
                    inputMode="numeric"
                    min={minimum}
                    step={1}
                    value={entry.amount}
                    aria-label={t(
                      "admin.entitlement.person.allocation.amount",
                      { provider: label },
                    )}
                    aria-invalid={invalid || overCommitted}
                    aria-describedby={invalid ? errorId : undefined}
                    onChange={(event) =>
                      onChange(provider.vendorId, {
                        amount: event.target.value,
                      })
                    }
                  />
                ) : (
                  <input
                    id={amountId}
                    type="text"
                    readOnly
                    value={formatNumber(readOnlyValue)}
                    aria-label={readOnlyLabel}
                  />
                )}
              </div>
              {showGroups && (
                <fieldset
                  className={`${styles.permissionFieldset} ${styles.allocationGroups}`}
                  aria-describedby={groupsMissing ? groupsErrorId : undefined}
                >
                  <legend>
                    {t("accessGroups.legend", { provider: label })}
                  </legend>
                  <div className={styles.permissionGrid}>
                    {(accessGroupCatalog?.[provider.vendorId] ?? []).map(
                      (group) => (
                        <label
                          key={group.id}
                          className={styles.permissionOption}
                        >
                          <input
                            type="checkbox"
                            checked={entry.accessGroups.includes(group.id)}
                            onChange={(event) =>
                              onChange(provider.vendorId, {
                                accessGroups: event.target.checked
                                  ? [...entry.accessGroups, group.id]
                                  : entry.accessGroups.filter(
                                      (id) => id !== group.id,
                                    ),
                              })
                            }
                          />
                          <span>
                            {group.displayName}
                            {group.description !== "" && (
                              <span className={styles.note}>
                                {" "}
                                {group.description}
                              </span>
                            )}
                          </span>
                        </label>
                      ),
                    )}
                  </div>
                  {groupsMissing && (
                    <span
                      id={groupsErrorId}
                      className={`${styles.feedback} ${styles.allocationError}`}
                    >
                      {t("accessGroups.required")}
                    </span>
                  )}
                </fieldset>
              )}
              {invalid && (
                <span
                  id={errorId}
                  className={`${styles.feedback} ${styles.allocationError}`}
                >
                  {isValidAmount(entry.amount)
                    ? t(
                        "admin.entitlement.person.allocation.amountBelowMinimum",
                        "Must be at least {{minimum}}, which is already spent this month.",
                        { minimum: formatUsd(minimum) },
                      )
                    : t("admin.entitlement.person.allocation.amountInvalid")}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {overCommitted && (
        <p role="alert" className={styles.feedback}>
          {t("admin.entitlement.person.allocation.overCommitted", {
            over: formatUsd(-figures.remaining),
          })}
        </p>
      )}
    </>
  );
}
